import React from "react";
import { Wrench } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  EmptyState,
  formatDate,
  getStatusClasses,
  SectionCard,
  useTenantPortalData,
} from "./tenantPortalShared.jsx";

export default function TenantRequestsPage({ isDark, setIsDark, currency, setCurrency }) {
  const { user } = useAuth();
  const toast = useToast();
  const {
    tenant,
    maintenanceTickets,
    savingIssue,
    issueForm,
    setIssueForm,
    handleIssueSubmit,
  } = useTenantPortalData(user, toast);

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Resident Portal"
      title="My requests"
      description="Submit and track maintenance requests for your assigned property and unit."
    >
      <section className="grid gap-6 xl:grid-cols-[1.05fr_1.15fr]">
        <SectionCard
          title="Report maintenance issue"
          description="Submit a resident maintenance request for your assigned property and unit."
          isDark={isDark}
        >
          <form onSubmit={handleIssueSubmit} className="space-y-4">
            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Issue description
              </label>
              <textarea
                value={issueForm.issue}
                onChange={(event) => setIssueForm((current) => ({ ...current, issue: event.target.value }))}
                placeholder="Describe the issue in your unit or building area."
                className={`min-h-[140px] w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
                  isDark
                    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
                    : "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                }`}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  Priority
                </label>
                <select
                  value={issueForm.priority}
                  onChange={(event) => setIssueForm((current) => ({ ...current, priority: event.target.value }))}
                  className={`h-12 w-full rounded-2xl border px-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
                    isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-900"
                  }`}
                >
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>

              <div>
                <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  Linked location
                </label>
                <div
                  className={`flex h-12 items-center rounded-2xl border px-4 text-sm ${
                    isDark ? "border-slate-700 bg-slate-950 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-700"
                  }`}
                >
                  {tenant?.property_name && tenant?.unit ? `${tenant.property_name} | ${tenant.unit}` : "Not linked yet"}
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={savingIssue}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                savingIssue ? "cursor-not-allowed bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              <Wrench className="mr-2 h-4 w-4" />
              {savingIssue ? "Submitting..." : "Submit issue"}
            </button>
          </form>
        </SectionCard>

        <SectionCard
          title="My maintenance requests"
          description="Resident requests already logged for your assigned property and unit."
          isDark={isDark}
        >
          {maintenanceTickets.length === 0 ? (
            <EmptyState
              title="No resident issues yet"
              body="When you submit maintenance requests from the resident portal, they will appear here."
              isDark={isDark}
            />
          ) : (
            <div className="space-y-3">
              {maintenanceTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`rounded-2xl border p-4 ${
                    isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{ticket.issue}</p>
                      <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {ticket.id} | Reported {formatDate(ticket.reported_on)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(ticket.status)}`}>
                        {ticket.status}
                      </span>
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                          ticket.priority === "High"
                            ? "bg-rose-100 text-rose-700"
                            : ticket.priority === "Medium"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-sky-100 text-sky-700"
                        }`}
                      >
                        {ticket.priority}
                      </span>
                    </div>
                  </div>
                  <div className={`mt-3 flex flex-wrap gap-4 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    <span>Category: {ticket.category}</span>
                    <span>Assigned: {ticket.assigned || "Awaiting assignment"}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </section>
    </AppLayout>
  );
}
